// CPAP 処方指示書 フィールド定義・条件ルール・オーダー種別定数
// ソース: docs/cpap_instruction_spec_philips_dreamstation.json (71項目)

// ---------------------------------------------------------------------------
// 型定義
// ---------------------------------------------------------------------------

/** 権限レベル */
export type PermissionLevel = "Editable" | "ReadOnly" | "NA";

/** 4ロール分の権限マップ */
export type FieldPermissions = {
  patient: PermissionLevel;
  doctor: PermissionLevel;
  medical_clerk: PermissionLevel;
  bs_operator: PermissionLevel;
};

/** 入力種別 */
export type CpapInputType = "text" | "select" | "date" | "fixed" | "auto" | "button";

/** 項目の用途区分（処方入力か患者同意か） */
export type CpapFieldScope = "prescription" | "consent";

/** 値を送る先の JSONB コンテンツ入れ物 */
export type CpapPayloadTarget = "prescriptionContent" | "inventoryContent" | "consent";

/** フィールド定義 */
export type CpapFieldDef = {
  no: number;
  category: string | null;
  name: string;
  key: string;
  inputType: CpapInputType;
  required: "必須" | "任意" | "条件付必須" | "ー";
  attribute: string;
  maxLength: number | string;
  permissions: FieldPermissions;
  options?: string[];
  defaultValue?: string | number | null;
  minValue?: number;
  maxValue?: number;
  format?: string;
  autoGenerated?: boolean;
  /** 処方入力 or 患者同意（省略時は prescription 扱い） */
  scope?: CpapFieldScope;
  /** 値の送信先コンテンツ（省略時は非送信）。topLevel 送信は topLevel フラグで別途指定 */
  payloadTarget?: CpapPayloadTarget;
  /** リクエスト直下（top-level）にも送るか */
  topLevel?: boolean;
};

/** セクション定義 */
export type CpapSection = {
  id: string;
  label: string;
  fields: number[];
};

/** 条件ルールの効果 */
export type ConditionalEffect = "editable" | "disabled" | "required";

/** 条件ルール */
export type ConditionalRule = {
  targetNo: number;
  sourceNo: number;
  when?: { value: string };
  whenNot?: { value: string };
  whenFilled?: boolean;
  whenNotFilled?: boolean;
  effect: ConditionalEffect;
};

/** オーダー種別振る舞い */
export type OrderTypeBehavior = {
  prefill: "defaults" | "latest" | "beforeStop";
  editableOverrides: { only: number[] } | null;
  clearFields?: number[];
  disableFields?: number[];
};

export type CpapManufacturer = "フィリップス" | "チェスト(ムラタ)";

export type CpapFormConfig = {
  manufacturer: CpapManufacturer;
  fields: CpapFieldDef[];
  sections: CpapSection[];
  conditionalRules: ConditionalRule[];
  orderTypeBehaviors: Record<string, OrderTypeBehavior>;
  maskCushionOptions: Record<string, string[]>;
};

// ---------------------------------------------------------------------------
// CPAP_FIELDS — 71項目全定義
// ---------------------------------------------------------------------------

export const CPAP_FIELDS: CpapFieldDef[] = [
  // ===== 基本情報 (No.1-4) =====
  {
    no: 1,
    category: "基本情報",
    name: "管理番号",
    key: "orderNumber",
    inputType: "auto",
    required: "必須",
    attribute: "半角数字",
    maxLength: 7,
    permissions: {
      patient: "NA",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    autoGenerated: true,
  },
  {
    no: 2,
    category: "基本情報",
    name: "オーダー種別",
    key: "orderType",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 8,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["新規", "処方変更", "マスク変更", "機種変更", "中止", "再開"],
  },
  {
    no: 3,
    category: "基本情報",
    name: "中止理由",
    key: "cancellationReason",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 4,
    category: "基本情報",
    name: "指示日",
    key: "instructionDate",
    inputType: "date",
    required: "必須",
    attribute: "半角数字記号",
    maxLength: 10,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    format: "yyyy/MM/dd",
  },
  {
    no: 72,
    category: "基本情報",
    name: "算定開始年",
    key: "billingStartYear",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 4,
    minValue: 1900,
    maxValue: 2100,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 73,
    category: "基本情報",
    name: "算定開始月",
    key: "billingStartMonth",
    inputType: "select",
    required: "必須",
    options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
    attribute: "半角数字",
    maxLength: 2,
    minValue: 1,
    maxValue: 12,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },

  // ===== 医療機関情報 (No.5-8) =====
  {
    no: 5,
    category: "医療機関情報",
    name: "医療機関名",
    key: "clinicName",
    inputType: "text",
    required: "必須",
    attribute: "全角",
    maxLength: 60,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 6,
    category: "医療機関情報",
    name: "診療科",
    key: "department",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 7,
    category: "医療機関情報",
    name: "医師",
    key: "doctorUserId",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    // options は動的（担当医一覧から取得）
  },
  {
    no: 8,
    category: "医療機関情報",
    name: "事務担当",
    key: "adminStaff",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },

  // ===== 使用者情報 (No.9-22) =====
  {
    no: 9,
    category: "使用者情報",
    name: "フリガナ",
    key: "patientKana",
    inputType: "text",
    required: "必須",
    attribute: "全角カナ",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 10,
    category: "使用者情報",
    name: "氏名",
    key: "patientName",
    inputType: "text",
    required: "必須",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 11,
    category: "使用者情報",
    name: "性別",
    key: "gender",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["男", "女"],
  },
  {
    no: 12,
    category: "使用者情報",
    name: "生年月日",
    key: "birthDate",
    inputType: "date",
    required: "必須",
    attribute: "半角数字記号",
    maxLength: 10,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    format: "yyyy/MM/dd",
  },
  {
    no: 13,
    category: "使用者情報",
    name: "郵便番号",
    key: "postalCode",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 7,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 14,
    category: "使用者情報",
    name: "住所",
    key: "address",
    inputType: "text",
    required: "必須",
    attribute: "全角",
    maxLength: 80,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 15,
    category: "使用者情報",
    name: "連絡先（携帯）",
    key: "phoneMobile",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: "10or11",
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 16,
    category: "使用者情報",
    name: "連絡先（その他）",
    key: "phoneOther",
    inputType: "text",
    required: "任意",
    attribute: "半角数字",
    maxLength: "10or11",
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 17,
    category: "使用者情報",
    name: "診察券番号/患者ID",
    key: "patientLocalId",
    inputType: "text",
    required: "必須",
    attribute: "半角英数字",
    maxLength: 10,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 18,
    category: "使用者情報",
    name: "フォローアップコール",
    key: "followUpCall",
    inputType: "select",
    required: "任意",
    attribute: "全角",
    maxLength: 6,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["する", "しない"],
    defaultValue: "する",
  },
  {
    no: 19,
    category: "使用者情報",
    name: "機器納品先",
    key: "deliveryDestination",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 8,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["患者自宅", "医療機関", "貸出済"],
    defaultValue: "患者自宅",
  },
  {
    no: 20,
    category: "使用者情報",
    name: "病名",
    key: "diseaseName",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: "睡眠時無呼吸症候群",
  },
  {
    no: 21,
    category: "使用者情報",
    name: "感染症有無",
    key: "infectionStatus",
    inputType: "select",
    required: "任意",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["無", "有"],
  },
  {
    no: 22,
    category: "使用者情報",
    name: "感染症有無（疾患名）",
    key: "infectionDiseaseName",
    inputType: "text",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },

  // ===== 持続陽圧呼吸療法装置 (No.23-46) =====
  {
    no: 23,
    category: "持続陽圧呼吸療法装置",
    name: "機器",
    key: "device",
    inputType: "fixed",
    required: "ー",
    attribute: "全角",
    maxLength: 24,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    defaultValue: "ドリームステーションAuto",
  },
  {
    no: 24,
    category: "持続陽圧呼吸療法装置",
    name: "モード",
    key: "mode",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 14,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["AutoCPAPモード", "CPAPモード"],
    defaultValue: "AutoCPAPモード",
  },
  {
    no: 25,
    category: "持続陽圧呼吸療法装置",
    name: "Opti-Start",
    key: "optiStart",
    inputType: "select",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 26,
    category: "持続陽圧呼吸療法装置",
    name: "最大圧",
    key: "maxPressure",
    inputType: "text",
    required: "条件付必須",
    attribute: "半角数字",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: 20,
    minValue: 2,
    maxValue: 20,
  },
  {
    no: 27,
    category: "持続陽圧呼吸療法装置",
    name: "最小圧",
    key: "minPressure",
    inputType: "text",
    required: "条件付必須",
    attribute: "半角数字",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: 4,
    minValue: 1,
    maxValue: 19,
  },
  {
    no: 28,
    category: "持続陽圧呼吸療法装置",
    name: "Flexタイプ（AutoCPAPモード）",
    key: "flexTypeAuto",
    inputType: "select",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 7,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["A-Flex", "C-Flex", "0（オフ）"],
    defaultValue: "A-Flex",
  },
  {
    no: 29,
    category: "持続陽圧呼吸療法装置",
    name: "EZ-Start",
    key: "ezStart",
    inputType: "select",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 30,
    category: "持続陽圧呼吸療法装置",
    name: "CPAP圧",
    key: "cpapPressure",
    inputType: "text",
    required: "条件付必須",
    attribute: "半角数字",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: 4,
    minValue: 1,
    maxValue: 20,
  },
  {
    no: 31,
    category: "持続陽圧呼吸療法装置",
    name: "Flexタイプ（CPAPモード）",
    key: "flexTypeCpap",
    inputType: "select",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 7,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["C-Flex+", "C-Flex", "0（オフ）"],
    defaultValue: "C-Flex+",
  },
  {
    no: 32,
    category: "持続陽圧呼吸療法装置",
    name: "SmartRamp機能",
    key: "smartRamp",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 33,
    category: "持続陽圧呼吸療法装置",
    name: "ランプ時間",
    key: "rampTime",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: 0,
    minValue: 0,
    maxValue: 99,
  },
  {
    no: 34,
    category: "持続陽圧呼吸療法装置",
    name: "Flex設定",
    key: "flexSetting",
    inputType: "select",
    required: "必須",
    attribute: "半角数字",
    maxLength: 1,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["1", "2", "3"],
    defaultValue: 2,
  },
  {
    no: 35,
    category: "持続陽圧呼吸療法装置",
    name: "呼吸回路タイプ",
    key: "breathingCircuitType",
    inputType: "select",
    required: "必須",
    attribute: "半角英数字",
    maxLength: 3,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["15", "15H"],
    defaultValue: 15,
  },
  {
    no: 36,
    category: "持続陽圧呼吸療法装置",
    name: "マスクタイプ",
    key: "maskType",
    inputType: "select",
    required: "必須",
    attribute: "半角英数字",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["0", "X1", "X2", "X3", "X4", "X5"],
    defaultValue: 0,
  },
  {
    no: 37,
    category: "持続陽圧呼吸療法装置",
    name: "マスクフィットチェック",
    key: "maskFitCheck",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 38,
    category: "持続陽圧呼吸療法装置",
    name: "hPa/㎝H₂O",
    key: "pressureUnit",
    inputType: "select",
    required: "必須",
    attribute: "半角英数字",
    maxLength: 5,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["hPa", "cmH2O"],
    defaultValue: "hPa",
  },
  {
    no: 39,
    category: "持続陽圧呼吸療法装置",
    name: "オートオフ",
    key: "autoOff",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 40,
    category: "持続陽圧呼吸療法装置",
    name: "ランプスタート圧",
    key: "rampStartPressure",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: 4,
    minValue: 1,
    maxValue: 20,
  },
  {
    no: 41,
    category: "持続陽圧呼吸療法装置",
    name: "Flex設定ロック",
    key: "flexSettingLock",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オン", "オフ"],
    defaultValue: "オン",
  },
  {
    no: 42,
    category: "持続陽圧呼吸療法装置",
    name: "呼吸回路タイプロック",
    key: "circuitTypeLock",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 43,
    category: "持続陽圧呼吸療法装置",
    name: "マスクタイプロック",
    key: "maskTypeLock",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 44,
    category: "持続陽圧呼吸療法装置",
    name: "AHI/Fit/PBの表示",
    key: "ahiFitPbDisplay",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オフ", "オン"],
    defaultValue: "オフ",
  },
  {
    no: 45,
    category: "持続陽圧呼吸療法装置",
    name: "オートオン",
    key: "autoOn",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["オン", "オフ"],
    defaultValue: "オン",
  },
  {
    no: 46,
    category: "持続陽圧呼吸療法装置",
    name: "言語設定",
    key: "language",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 9,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["JP", "0（アイコン）", "EN", "CN"],
    defaultValue: "JP",
  },

  // ===== 関連用品 (No.47-59) =====
  {
    no: 47,
    category: "関連用品",
    name: "加温加湿器",
    key: "humidifier",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["無", "有"],
    defaultValue: "無",
  },
  {
    no: 48,
    category: "関連用品",
    name: "機種",
    key: "humidifierModel",
    inputType: "fixed",
    required: "ー",
    attribute: "全角",
    maxLength: 32,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    defaultValue: "ドリームステーション用加温加湿器",
  },
  {
    no: 49,
    category: "関連用品",
    name: "加湿モード",
    key: "humidityMode",
    inputType: "select",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 26,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["標準", "アドバンス", "ヒーターチューブ式加温加湿"],
    defaultValue: "アドバンス",
  },
  {
    no: 50,
    category: "関連用品",
    name: "加湿器設定",
    key: "humidifierSetting",
    inputType: "text",
    required: "条件付必須",
    attribute: "半角数字",
    maxLength: 1,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: 3,
    minValue: 0,
    maxValue: 3,
  },
  {
    no: 51,
    category: "関連用品",
    name: "回路温度設定",
    key: "circuitTemperature",
    inputType: "text",
    required: "条件付必須",
    attribute: "半角数字",
    maxLength: 1,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    defaultValue: 3,
    minValue: 0,
    maxValue: 3,
  },
  {
    no: 52,
    category: "関連用品",
    name: "通信モジュール",
    key: "communicationModule",
    inputType: "fixed",
    required: "ー",
    attribute: "全角",
    maxLength: 2,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    defaultValue: "有",
  },
  {
    no: 53,
    category: "関連用品",
    name: "患者情報管理",
    key: "patientInfoManagement",
    inputType: "fixed",
    required: "ー",
    attribute: "全角",
    maxLength: 24,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    defaultValue: "ケア　オーケストレーター",
  },
  {
    no: 54,
    category: "関連用品",
    name: "登録方法",
    key: "registrationMethod",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 22,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["機器取扱企業にて登録", "医療施設にて登録"],
    defaultValue: "機器取扱企業にて登録",
  },
  {
    no: 55,
    category: "関連用品",
    name: "マスク種類",
    key: "maskTypeName",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 22,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: [
      "ウィスプネーザルマスク",
      "ドリームウェア（ネーザル）",
      "ドリームウェアシリコンピローマスク",
      "アマラフルフェイスマスク",
      "ピコネーザルマスク（取扱い終了）",
    ],
    defaultValue: "ウィスプネーザルマスク",
  },
  {
    no: 56,
    category: "関連用品",
    name: "マスクフレームサイズ",
    key: "maskFrameSize",
    inputType: "select",
    required: "条件付必須",
    attribute: "半角英字記号",
    maxLength: 3,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: [
      "SMフレーム（S相当）",
      "MEDフレーム（M相当）",
      "LGフレーム（L相当）",
    ],
  },
  {
    no: 57,
    category: "関連用品",
    name: "マスククッションサイズ",
    key: "maskCushionSize",
    inputType: "select",
    required: "必須",
    attribute: "半角英字記号",
    maxLength: 3,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    // options は動的（MASK_CUSHION_OPTIONS で No.55 に連動）
  },
  {
    no: 58,
    category: "関連用品",
    name: "マスク固定方法",
    key: "maskFixationMethod",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 59,
    category: "関連用品",
    name: "仕様回路",
    key: "circuitTypeName",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 22,
    permissions: {
      patient: "ReadOnly",
      doctor: "Editable",
      medical_clerk: "Editable",
      bs_operator: "ReadOnly",
    },
    options: ["15mm", "ヒ―テッドチューブ15㎜"],
    defaultValue: "15mm",
  },

  // ===== 固定情報 (No.60-61) =====
  {
    no: 60,
    category: "固定情報",
    name: "業務受託会社",
    key: "outsourcingCompany",
    inputType: "fixed",
    required: "ー",
    attribute: "全角",
    maxLength: 29,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    defaultValue: "NTT西日本㈱",
  },
  {
    no: 61,
    category: "固定情報",
    name: "メーカー名",
    key: "manufacturer",
    inputType: "fixed",
    required: "ー",
    attribute: "全角",
    maxLength: 24,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    defaultValue: "㈱フィリップス・ジャパン",
  },

  // ===== 患者 (No.62-63) — ExecPlan 3 スコープだが定義は含める =====
  {
    no: 62,
    category: "患者",
    name: "同意ボタン",
    key: "consentButton",
    inputType: "button",
    required: "必須",
    attribute: "ー",
    maxLength: 1,
    permissions: {
      patient: "Editable",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
  },
  {
    no: 63,
    category: "患者",
    name: "患者様同意日",
    key: "patientConsentDate",
    inputType: "date",
    required: "ー",
    attribute: "半角数字記号",
    maxLength: 10,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "ReadOnly",
    },
    format: "yyyy/MM/dd",
  },

  // ===== 在庫管理 (No.64-71) =====
  {
    no: 64,
    category: "在庫管理",
    name: "ドリームステーションS/N",
    key: "dreamstationSerial",
    inputType: "text",
    required: "ー",
    attribute: "半角英数字",
    maxLength: 13,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
  },
  {
    no: 65,
    category: "在庫管理",
    name: "CPAP設置日",
    key: "cpapInstallDate",
    inputType: "date",
    required: "条件付必須",
    attribute: "半角数字記号",
    maxLength: 10,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
    format: "yyyy/MM/dd",
  },
  {
    no: 66,
    category: "在庫管理",
    name: "通信モジュールS/N",
    key: "commModuleSerial",
    inputType: "text",
    required: "ー",
    attribute: "半角英数字",
    maxLength: 13,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
  },
  {
    no: 67,
    category: "在庫管理",
    name: "通信モジュール設置日",
    key: "commModuleInstallDate",
    inputType: "date",
    required: "条件付必須",
    attribute: "半角数字記号",
    maxLength: 10,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
    format: "yyyy/MM/dd",
  },
  {
    no: 68,
    category: "在庫管理",
    name: "CPAP本体設置日と同日",
    key: "commModuleSameAsCpap",
    inputType: "button",
    required: "任意",
    attribute: "ー",
    maxLength: 1,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
  },
  {
    no: 69,
    category: "在庫管理",
    name: "加温加湿器S/N",
    key: "humidifierSerial",
    inputType: "text",
    required: "ー",
    attribute: "半角英数字",
    maxLength: 13,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
  },
  {
    no: 70,
    category: "在庫管理",
    name: "加温加湿器設置日",
    key: "humidifierInstallDate",
    inputType: "date",
    required: "任意",
    attribute: "半角数字記号",
    maxLength: 10,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
    format: "yyyy/MM/dd",
  },
  {
    no: 71,
    category: "在庫管理",
    name: "CPAP本体設置日と同日",
    key: "humidifierSameAsCpap",
    inputType: "button",
    required: "条件付必須",
    attribute: "ー",
    maxLength: 1,
    permissions: {
      patient: "ReadOnly",
      doctor: "ReadOnly",
      medical_clerk: "ReadOnly",
      bs_operator: "Editable",
    },
  },
];

// ---------------------------------------------------------------------------
// CPAP_SECTIONS — 8セクション（ExecPlan 準拠）
// ---------------------------------------------------------------------------

export const CPAP_SECTIONS: CpapSection[] = [
  { id: "basic", label: "基本情報", fields: [1, 2, 3, 4, 72, 73] },
  { id: "clinic", label: "医療機関情報", fields: [5, 6, 7, 8] },
  {
    id: "patient",
    label: "使用者情報",
    fields: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22],
  },
  {
    id: "cpap_device",
    label: "持続陽圧呼吸療法装置",
    fields: [
      23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
      41, 42, 43, 44, 45, 46,
    ],
  },
  {
    id: "accessories",
    label: "関連用品",
    fields: [47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59],
  },
  { id: "fixed", label: "固定情報", fields: [60, 61] },
  { id: "consent_info", label: "同意情報", fields: [63] },
  {
    id: "inventory",
    label: "在庫管理",
    fields: [64, 65, 66, 67, 68, 69, 70, 71],
  },
];

// ---------------------------------------------------------------------------
// CPAP_CONDITIONAL_RULES — 条件ルール
// ---------------------------------------------------------------------------

export const CPAP_CONDITIONAL_RULES: ConditionalRule[] = [
  // No.3 中止理由 ← No.2 オーダー種別
  { targetNo: 3, sourceNo: 2, when: { value: "中止" }, effect: "editable" },
  { targetNo: 3, sourceNo: 2, whenNot: { value: "中止" }, effect: "disabled" },

  // No.22 感染症有無（疾患名） ← No.21 感染症有無
  { targetNo: 22, sourceNo: 21, when: { value: "有" }, effect: "required" },
  { targetNo: 22, sourceNo: 21, whenNot: { value: "有" }, effect: "disabled" },

  // No.25-28 AutoCPAP系 ← No.24 モード
  {
    targetNo: 25,
    sourceNo: 24,
    when: { value: "AutoCPAPモード" },
    effect: "required",
  },
  {
    targetNo: 25,
    sourceNo: 24,
    when: { value: "CPAPモード" },
    effect: "disabled",
  },
  {
    targetNo: 26,
    sourceNo: 24,
    when: { value: "AutoCPAPモード" },
    effect: "required",
  },
  {
    targetNo: 26,
    sourceNo: 24,
    when: { value: "CPAPモード" },
    effect: "disabled",
  },
  {
    targetNo: 27,
    sourceNo: 24,
    when: { value: "AutoCPAPモード" },
    effect: "required",
  },
  {
    targetNo: 27,
    sourceNo: 24,
    when: { value: "CPAPモード" },
    effect: "disabled",
  },
  {
    targetNo: 28,
    sourceNo: 24,
    when: { value: "AutoCPAPモード" },
    effect: "required",
  },
  {
    targetNo: 28,
    sourceNo: 24,
    when: { value: "CPAPモード" },
    effect: "disabled",
  },

  // No.29-31 CPAP系 ← No.24 モード
  {
    targetNo: 29,
    sourceNo: 24,
    when: { value: "CPAPモード" },
    effect: "required",
  },
  {
    targetNo: 29,
    sourceNo: 24,
    when: { value: "AutoCPAPモード" },
    effect: "disabled",
  },
  {
    targetNo: 30,
    sourceNo: 24,
    when: { value: "CPAPモード" },
    effect: "required",
  },
  {
    targetNo: 30,
    sourceNo: 24,
    when: { value: "AutoCPAPモード" },
    effect: "disabled",
  },
  {
    targetNo: 31,
    sourceNo: 24,
    when: { value: "CPAPモード" },
    effect: "required",
  },
  {
    targetNo: 31,
    sourceNo: 24,
    when: { value: "AutoCPAPモード" },
    effect: "disabled",
  },

  // No.49-51 加湿系 ← No.47 加温加湿器
  { targetNo: 49, sourceNo: 47, when: { value: "有" }, effect: "required" },
  { targetNo: 49, sourceNo: 47, whenNot: { value: "有" }, effect: "disabled" },
  { targetNo: 50, sourceNo: 47, when: { value: "有" }, effect: "required" },
  { targetNo: 50, sourceNo: 47, whenNot: { value: "有" }, effect: "disabled" },
  { targetNo: 51, sourceNo: 47, when: { value: "有" }, effect: "required" },
  { targetNo: 51, sourceNo: 47, whenNot: { value: "有" }, effect: "disabled" },

  // No.56 マスクフレームサイズ ← No.55 マスク種類
  {
    targetNo: 56,
    sourceNo: 55,
    when: { value: "ドリームウェア（ネーザル）" },
    effect: "required",
  },
  {
    targetNo: 56,
    sourceNo: 55,
    whenNot: { value: "ドリームウェア（ネーザル）" },
    effect: "disabled",
  },

  // No.65 CPAP設置日 ← No.64 ドリームステーションS/N（入力済みなら必須）
  { targetNo: 65, sourceNo: 64, whenFilled: true, effect: "required" },

  // No.67 通信モジュール設置日 ← No.66 通信モジュールS/N
  { targetNo: 67, sourceNo: 66, whenFilled: true, effect: "required" },

  // No.70 加温加湿器設置日 ← No.69 加温加湿器S/N
  { targetNo: 70, sourceNo: 69, whenFilled: true, effect: "required" },
];

// 転記ボタンルール（No.68, No.71）は CpapPrescriptionForm 内で直接ハンドリング
// No.68: No.65 の日付 → No.67 へ転記
// No.71: No.65 の日付 → No.70 へ転記

// ---------------------------------------------------------------------------
// ORDER_TYPE_BEHAVIORS — 5つのオーダー種別振る舞い
//
// `prefill` は src/lib/cpap/prescriptionPrefill.ts が「どの過去処方を初期値の引き継ぎ元に
// するか」の判定に使う。メーカーによって引き継ぎ元が変わらないよう、フィリップス版と
// チェスト(ムラタ)版（CHEST_MURATA_ORDER_TYPE_BEHAVIORS）で値を一致させること
// （prescriptionPrefill.spec.ts のガードテストで検証している）。
// `clearFields` は逆に項番体系がメーカー固有なので、必ず該当メーカーの項目定義から引く。
// ---------------------------------------------------------------------------

export const ORDER_TYPE_BEHAVIORS: Record<string, OrderTypeBehavior> = {
  新規: { prefill: "defaults", editableOverrides: null },
  処方変更: {
    prefill: "latest",
    editableOverrides: null,
    disableFields: [72, 73],
  },
  マスク変更: {
    prefill: "latest",
    editableOverrides: { only: [4, 5, 6, 7, 8, 36, 43, 55, 56, 57, 58] },
  },
  機種変更: { prefill: "defaults", editableOverrides: null },
  中止: {
    prefill: "latest",
    editableOverrides: { only: [3, 4, 5, 6, 7, 8] },
  },
  再開: {
    prefill: "beforeStop",
    editableOverrides: null,
    clearFields: [3, 4, 7, 8, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73],
  },
};

// ---------------------------------------------------------------------------
// MASK_CUSHION_OPTIONS — マスク種別→クッションサイズ動的選択肢
// ---------------------------------------------------------------------------

export const MASK_CUSHION_OPTIONS: Record<string, string[]> = {
  ウィスプネーザルマスク: ["S/M", "Mワイド", "L", "XL"],
  "ドリームウェア（ネーザル）": ["S", "M", "L"],
  ドリームウェアシリコンピローマスク: ["S", "M", "L"],
  アマラフルフェイスマスク: ["S", "M", "L"],
  "ピコネーザルマスク（取扱い終了）": ["S/M", "L", "XL"],
};

const PHILIPS_FORM_CONFIG: CpapFormConfig = {
  manufacturer: "フィリップス",
  fields: CPAP_FIELDS,
  sections: CPAP_SECTIONS,
  conditionalRules: CPAP_CONDITIONAL_RULES,
  orderTypeBehaviors: ORDER_TYPE_BEHAVIORS,
  maskCushionOptions: MASK_CUSHION_OPTIONS,
};

// ===========================================================================
// チェスト(ムラタ)（ムラタ CPAP/MX）構成
// - 仕様ソース: docs/cpap_instruction_spec_murata_cpap_mx.(csv|json)
// - scope=consent（No.47-51）は同意画面が未決定のため sections・条件ルール対象外
// - payloadTarget/topLevel を明示し、splitFormDataForPayload のデータ駆動パスで分割
// ===========================================================================

// 権限プリセット（enterprise_admin は FieldPermissions に含めない）
const CHEST_MURATA_P_AUTO: FieldPermissions = {
  patient: "NA",
  doctor: "ReadOnly",
  medical_clerk: "ReadOnly",
  bs_operator: "ReadOnly",
};
const CHEST_MURATA_P_EDIT: FieldPermissions = {
  patient: "ReadOnly",
  doctor: "Editable",
  medical_clerk: "Editable",
  bs_operator: "ReadOnly",
};
const CHEST_MURATA_P_RO: FieldPermissions = {
  patient: "ReadOnly",
  doctor: "ReadOnly",
  medical_clerk: "ReadOnly",
  bs_operator: "ReadOnly",
};
const CHEST_MURATA_P_CONSENT: FieldPermissions = {
  patient: "Editable",
  doctor: "ReadOnly",
  medical_clerk: "ReadOnly",
  bs_operator: "ReadOnly",
};

export const CHEST_MURATA_FIELDS: CpapFieldDef[] = [
  // ===== 基本情報 (No.1-4) =====
  {
    no: 1,
    category: "基本情報",
    name: "管理番号",
    key: "orderNumber",
    inputType: "auto",
    required: "必須",
    attribute: "半角数字",
    maxLength: 7,
    permissions: CHEST_MURATA_P_AUTO,
    autoGenerated: true,
    scope: "prescription",
    topLevel: true,
  },
  {
    no: 2,
    category: "基本情報",
    name: "オーダー種別",
    key: "orderType",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 8,
    options: ["新規", "処方変更", "マスク変更", "機種変更", "中止", "再開"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    topLevel: true,
  },
  {
    no: 3,
    category: "基本情報",
    name: "中止理由",
    key: "cancellationReason",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 4,
    category: "基本情報",
    name: "指示日",
    key: "instructionDate",
    inputType: "date",
    required: "必須",
    attribute: "半角数字記号",
    maxLength: 10,
    format: "yyyy/MM/dd",
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    topLevel: true,
    payloadTarget: "prescriptionContent",
  },
  {
    no: 52,
    category: "基本情報",
    name: "算定開始年",
    key: "billingStartYear",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 4,
    minValue: 1900,
    maxValue: 2100,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    topLevel: true,
  },
  {
    no: 23,
    category: "基本情報",
    name: "算定開始月",
    key: "billingStartMonth",
    inputType: "select",
    required: "必須",
    options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
    attribute: "半角数字",
    maxLength: 2,
    minValue: 1,
    maxValue: 12,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    topLevel: true,
  },

  // ===== 医療機関情報 (No.5-8) =====
  {
    no: 5,
    category: "医療機関情報",
    name: "医療機関名",
    key: "clinicName",
    inputType: "text",
    required: "必須",
    attribute: "全角",
    maxLength: 60,
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 6,
    category: "医療機関情報",
    name: "診療科",
    key: "department",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 7,
    category: "医療機関情報",
    name: "医師",
    key: "doctorUserId",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    topLevel: true,
    payloadTarget: "prescriptionContent",
  },
  {
    no: 8,
    category: "医療機関情報",
    name: "事務担当",
    key: "adminStaff",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },

  // ===== 使用者情報 (No.9-22) =====
  {
    no: 9,
    category: "使用者情報",
    name: "フリガナ",
    key: "patientKana",
    inputType: "text",
    required: "必須",
    attribute: "全角カナ",
    maxLength: 40,
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 10,
    category: "使用者情報",
    name: "氏名",
    key: "patientName",
    inputType: "text",
    required: "必須",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 11,
    category: "使用者情報",
    name: "性別",
    key: "gender",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 2,
    options: ["男性", "女性"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 12,
    category: "使用者情報",
    name: "生年月日",
    key: "birthDate",
    inputType: "date",
    required: "必須",
    attribute: "半角数字記号",
    maxLength: 10,
    format: "yyyy/MM/dd",
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 13,
    category: "使用者情報",
    name: "郵便番号",
    key: "postalCode",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 7,
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 14,
    category: "使用者情報",
    name: "住所",
    key: "address",
    inputType: "text",
    required: "必須",
    attribute: "全角",
    maxLength: 80,
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 15,
    category: "使用者情報",
    name: "連絡先（携帯）",
    key: "phoneMobile",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: "10or11",
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 16,
    category: "使用者情報",
    name: "連絡先（その他）",
    key: "phoneOther",
    inputType: "text",
    required: "任意",
    attribute: "半角数字",
    maxLength: "10or11",
    permissions: CHEST_MURATA_P_RO,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 17,
    category: "使用者情報",
    name: "診察券番号/患者ID",
    key: "patientLocalId",
    inputType: "text",
    required: "必須",
    attribute: "半角英数字",
    maxLength: 10,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 18,
    category: "使用者情報",
    name: "フォローアップコール",
    key: "followUpCall",
    inputType: "select",
    required: "任意",
    attribute: "全角",
    maxLength: 6,
    options: ["する", "しない"],
    defaultValue: "する",
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 19,
    category: "使用者情報",
    name: "機器納品先",
    key: "deliveryDestination",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 8,
    defaultValue: "患者自宅",
    options: ["患者自宅", "医療機関", "貸出済"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 20,
    category: "使用者情報",
    name: "病名",
    key: "diseaseName",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    defaultValue: "睡眠時無呼吸症候群",
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 21,
    category: "使用者情報",
    name: "感染症有無",
    key: "infectionStatus",
    inputType: "select",
    required: "任意",
    attribute: "全角",
    maxLength: 4,
    options: ["無", "有"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 22,
    category: "使用者情報",
    name: "感染症有無（疾患名）",
    key: "infectionDiseaseName",
    inputType: "text",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },

  // ===== 基本設定 (No.24-29) =====
  {
    no: 24,
    category: "基本設定",
    name: "種類",
    key: "mode",
    inputType: "select",
    required: "必須",
    attribute: "半角英数字",
    maxLength: 9,
    options: ["CPAP", "Auto CPAP"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 25,
    category: "基本設定",
    name: "CPAP圧（cmH₂O）",
    key: "cpapPressure",
    inputType: "text",
    required: "条件付必須",
    attribute: "半角数字",
    maxLength: 4,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 26,
    category: "基本設定",
    name: "上限圧（cmH₂O）",
    key: "maxPressure",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 4,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 27,
    category: "基本設定",
    name: "下限圧（cmH₂O）",
    key: "minPressure",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 4,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 28,
    category: "基本設定",
    name: "ランプ設定 時間（分）",
    key: "rampTime",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 3,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 29,
    category: "基本設定",
    name: "ランプ設定 開始圧（cmH₂O）",
    key: "rampStartPressure",
    inputType: "text",
    required: "必須",
    attribute: "半角数字",
    maxLength: 4,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },

  // ===== 機種別設定 (No.30-35) =====
  {
    no: 30,
    category: "機種別設定",
    name: "機器",
    key: "device",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 20,
    defaultValue: "ムラタ CPAP/MX",
    options: ["ムラタ CPAP/MX"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 31,
    category: "機種別設定",
    name: "自動開始",
    key: "autoStart",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    options: ["オン", "オフ"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 32,
    category: "機種別設定",
    name: "自動停止",
    key: "autoStop",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    options: ["オン", "オフ"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 33,
    category: "機種別設定",
    name: "Flexレベル",
    key: "flexLevel",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    options: ["オフ", "1", "2", "3"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 34,
    category: "機種別設定",
    name: "オートタイプ",
    key: "autoType",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 8,
    options: ["ソフト", "ノーマル", "ハード"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 35,
    category: "機種別設定",
    name: "波形データ保存",
    key: "waveformDataSave",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 4,
    options: ["オン", "オフ"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },

  // ===== 付属品 (No.39-41) =====
  {
    no: 39,
    category: "付属品",
    name: "マスク種類",
    key: "maskTypeName",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 12,
    options: ["ネーザル", "フルフェイス", "ピロー"],
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 40,
    category: "付属品",
    name: "マスク品名",
    key: "maskProductName",
    inputType: "text",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },
  {
    no: 41,
    category: "付属品",
    name: "マスクサイズ",
    key: "maskSize",
    inputType: "text",
    required: "条件付必須",
    attribute: "全角",
    maxLength: 20,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },

  // ===== 備考 (No.46) =====
  {
    no: 46,
    category: "備考",
    name: "備考",
    key: "remarks",
    inputType: "text",
    required: "任意",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_EDIT,
    scope: "prescription",
    payloadTarget: "prescriptionContent",
  },

  // ===== 同意事項 (No.47-51) — 同意画面未決定のため sections 対象外・payload 非送出 =====
  {
    no: 47,
    category: "同意事項",
    name: "CPAPの使用について",
    key: "consentCpapUsage",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 10,
    options: ["同意する", "同意しない"],
    permissions: CHEST_MURATA_P_CONSENT,
    scope: "consent",
    payloadTarget: "consent",
  },
  {
    no: 48,
    category: "同意事項",
    name: "遠隔モニタリングについて",
    key: "consentRemoteMonitoring",
    inputType: "select",
    required: "必須",
    attribute: "全角",
    maxLength: 10,
    options: ["同意する", "同意しない"],
    permissions: CHEST_MURATA_P_CONSENT,
    scope: "consent",
    payloadTarget: "consent",
  },
  {
    no: 49,
    category: "同意事項",
    name: "同意年月日",
    key: "patientConsentDate",
    inputType: "date",
    required: "必須",
    attribute: "半角数字記号",
    maxLength: 10,
    format: "yyyy/MM/dd",
    permissions: CHEST_MURATA_P_CONSENT,
    scope: "consent",
    payloadTarget: "consent",
  },
  {
    no: 50,
    category: "同意事項",
    name: "患者氏名",
    key: "consentPatientName",
    inputType: "text",
    required: "必須",
    attribute: "全角",
    maxLength: 40,
    permissions: CHEST_MURATA_P_CONSENT,
    scope: "consent",
    payloadTarget: "consent",
  },
  {
    no: 51,
    category: "同意事項",
    name: "同意ボタン",
    key: "consentButton",
    inputType: "button",
    required: "必須",
    attribute: "全角",
    maxLength: 0,
    permissions: CHEST_MURATA_P_CONSENT,
    scope: "consent",
    payloadTarget: "consent",
  },
];

export const CHEST_MURATA_SECTIONS: CpapSection[] = [
  { id: "basic", label: "基本情報", fields: [1, 2, 3, 4, 52, 23] },
  { id: "clinic", label: "医療機関情報", fields: [5, 6, 7, 8] },
  {
    id: "patient",
    label: "使用者情報",
    fields: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22],
  },
  { id: "basic_settings", label: "基本設定", fields: [24, 25, 26, 27, 28, 29] },
  {
    id: "device_settings",
    label: "機種別設定",
    fields: [30, 31, 32, 33, 34, 35],
  },
  {
    id: "accessories",
    label: "付属品",
    fields: [39, 40, 41],
  },
  { id: "remarks", label: "備考", fields: [46] },
];

export const CHEST_MURATA_CONDITIONAL_RULES: ConditionalRule[] = [
  // No.3 中止理由 ← No.2 オーダー種別
  { targetNo: 3, sourceNo: 2, when: { value: "中止" }, effect: "editable" },
  { targetNo: 3, sourceNo: 2, whenNot: { value: "中止" }, effect: "disabled" },

  // No.22 感染症有無（疾患名） ← No.21 感染症有無
  { targetNo: 22, sourceNo: 21, when: { value: "有" }, effect: "required" },
  { targetNo: 22, sourceNo: 21, whenNot: { value: "有" }, effect: "disabled" },

  // No.25 CPAP圧 ← No.24 種類（CPAP のときのみ有効・必須。それ以外は無効）
  { targetNo: 25, sourceNo: 24, when: { value: "CPAP" }, effect: "required" },
  {
    targetNo: 25,
    sourceNo: 24,
    whenNot: { value: "CPAP" },
    effect: "disabled",
  },

  // No.40 マスク品名 ← No.39 マスク種類（選択済みのときのみ有効・必須）
  { targetNo: 40, sourceNo: 39, whenFilled: true, effect: "required" },
  { targetNo: 40, sourceNo: 39, whenNotFilled: true, effect: "disabled" },

  // No.41 マスクサイズ ← No.39 マスク種類（選択済みのときのみ有効・必須）
  { targetNo: 41, sourceNo: 39, whenFilled: true, effect: "required" },
  { targetNo: 41, sourceNo: 39, whenNotFilled: true, effect: "disabled" },
];

// 暫定: オーダー種別の振る舞いはフィリップス版に準拠しつつ、ムラタの項番へ読み替え。
// 確定仕様が出たら再構造化する（Decision Log 参照）。
// `prefill` は ORDER_TYPE_BEHAVIORS と必ず同じ値にすること（理由は同定数のコメント参照）。
// `clearFields` の項番はムラタ固有（例: No.23 は算定開始月。フィリップスの No.23 は機器）。
export const CHEST_MURATA_ORDER_TYPE_BEHAVIORS: Record<string, OrderTypeBehavior> = {
  新規: { prefill: "defaults", editableOverrides: null },
  処方変更: {
    prefill: "latest",
    editableOverrides: null,
    disableFields: [52, 23],
  },
  マスク変更: {
    prefill: "latest",
    editableOverrides: { only: [4, 5, 6, 7, 8, 39, 40, 41] },
  },
  機種変更: { prefill: "defaults", editableOverrides: null },
  中止: {
    prefill: "latest",
    editableOverrides: { only: [3, 4, 5, 6, 7, 8] },
  },
  再開: {
    prefill: "beforeStop",
    editableOverrides: null,
    clearFields: [3, 4, 7, 8, 52, 23],
  },
};

const CHEST_MURATA_FORM_CONFIG: CpapFormConfig = {
  manufacturer: "チェスト(ムラタ)",
  fields: CHEST_MURATA_FIELDS,
  sections: CHEST_MURATA_SECTIONS,
  conditionalRules: CHEST_MURATA_CONDITIONAL_RULES,
  orderTypeBehaviors: CHEST_MURATA_ORDER_TYPE_BEHAVIORS,
  maskCushionOptions: {},
};

export const CPAP_DEVICE_OPTIONS: CpapManufacturer[] = [
  PHILIPS_FORM_CONFIG.manufacturer,
  CHEST_MURATA_FORM_CONFIG.manufacturer,
];

export function getCpapFormConfig(device?: string): CpapFormConfig {
  switch (device) {
    case "チェスト(ムラタ)":
      return CHEST_MURATA_FORM_CONFIG;
    case undefined:
    case "":
    case "フィリップス":
    default:
      return PHILIPS_FORM_CONFIG;
  }
}

// ---------------------------------------------------------------------------
// ユーティリティ: フィールド番号→定義のルックアップマップ
// ---------------------------------------------------------------------------

const fieldByNo = new Map<number, CpapFieldDef>();
for (const f of CPAP_FIELDS) fieldByNo.set(f.no, f);

export function getFieldByNo(
  no: number,
  fields: CpapFieldDef[] = CPAP_FIELDS,
): CpapFieldDef | undefined {
  if (fields === CPAP_FIELDS) {
    return fieldByNo.get(no);
  }
  return fields.find((field) => field.no === no);
}

export function getFieldByKey(
  key: string,
  fields: CpapFieldDef[] = CPAP_FIELDS,
): CpapFieldDef | undefined {
  return fields.find((f) => f.key === key);
}

// ---------------------------------------------------------------------------
// Payload 分離ヘルパー: prescriptionContent / inventoryContent
// ---------------------------------------------------------------------------

/** inventoryContent に含めるフィールド番号 (No.64-71) */
const INVENTORY_FIELD_NOS = new Set(
  CPAP_FIELDS.filter((f) => f.no >= 64 && f.no <= 71).map((f) => f.no),
);

/** top-level で送るキー (No.1, 2, 4, 7 の一部) */
const TOP_LEVEL_KEYS = new Set([
  "orderNumber",
  "orderType",
  "instructionDate",
  "doctorUserId",
  "billingStartYear",
  "billingStartMonth",
]);

/**
 * prescriptionContent に保存される項目だけを返す。
 *
 * 判定規則は 2 段構え。
 * - `payloadTarget` / `topLevel` の明示が 1 つでもある構成（チェスト(ムラタ)）は
 *   `payloadTarget === "prescriptionContent"` の項目を対象とする。
 * - 明示がない従来構成（フィリップス）は項目番号 No.3-61 を対象とする。
 *
 * 保存処理 `splitFormDataForPayload` と、処方内容の差分算出
 * (`src/lib/cpap/prescriptionDiff.ts`) の両方がこの関数を使う。
 * 「prescriptionContent に入っている項目」と「差分対象の項目」を常に一致させるため、
 * 規則をこの 1 か所に集約している。
 */
export function getPrescriptionContentFields(
  fields: CpapFieldDef[] = CPAP_FIELDS,
): CpapFieldDef[] {
  const anyExplicit = fields.some(
    (f) => f.payloadTarget !== undefined || f.topLevel !== undefined,
  );

  if (anyExplicit) {
    return fields.filter((f) => f.payloadTarget === "prescriptionContent");
  }

  return fields.filter((f) => f.no >= 3 && f.no <= 61);
}

/**
 * formData を prescriptionContent / inventoryContent / topLevel に分割する
 */
export function splitFormDataForPayload(
  cpapDevice: string,
  formData: Record<string, unknown>,
  fields: CpapFieldDef[] = CPAP_FIELDS,
): {
  topLevel: Record<string, unknown>;
  prescriptionContent: Record<string, unknown>;
  inventoryContent: Record<string, unknown>;
} {
  const topLevel: Record<string, unknown> = {};
  const prescriptionContent: Record<string, unknown> = {};
  const inventoryContent: Record<string, unknown> = {};

  // prescriptionContent の判定は getPrescriptionContentFields に集約している
  // （差分算出と規則を共有するため）。
  const prescriptionFieldNos = new Set(
    getPrescriptionContentFields(fields).map((f) => f.no),
  );

  // フィールドが payloadTarget / topLevel を明示している場合はデータ駆動で分割する。
  // 明示がない従来構成（フィリップス）は番号レンジ推定の後方互換パスを維持する。
  const anyExplicit = fields.some(
    (f) => f.payloadTarget !== undefined || f.topLevel !== undefined,
  );

  if (anyExplicit) {
    for (const field of fields) {
      const val = formData[field.key];
      if (val === undefined) continue;

      if (field.topLevel) {
        topLevel[field.key] = val;
      }
      if (prescriptionFieldNos.has(field.no)) {
        prescriptionContent[field.key] = val;
      } else if (field.payloadTarget === "inventoryContent") {
        inventoryContent[field.key] = val;
      }
      // payloadTarget === "consent" / undefined はコンテンツへ載せない
    }

    return {
      topLevel,
      prescriptionContent: { ...prescriptionContent, cpapDevice: cpapDevice },
      inventoryContent,
    };
  }

  const inventoryFieldNos =
    fields === CPAP_FIELDS
      ? INVENTORY_FIELD_NOS
      : new Set(
          fields.filter((f) => f.no >= 64 && f.no <= 71).map((f) => f.no),
        );

  for (const field of fields) {
    const val = formData[field.key];
    if (val === undefined) continue;

    if (TOP_LEVEL_KEYS.has(field.key)) {
      topLevel[field.key] = val;
    }
    if (prescriptionFieldNos.has(field.no)) {
      prescriptionContent[field.key] = val;
    }
    if (inventoryFieldNos.has(field.no)) {
      inventoryContent[field.key] = val;
    }
  }

  return {
    topLevel,
    prescriptionContent: { ...prescriptionContent, cpapDevice: cpapDevice },
    inventoryContent,
  };
}
